import useLocale from 'next-translate/useTranslation';

export default function AgentCapabilities() {
  const { t } = useLocale('chatbots');

  return (
    <section className='bg-white py-24 dark:bg-slate-900 sm:py-32'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='max-w-3xl'>
          <h2 className='text-sm font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400'>
            {t('The full toolkit')}
          </h2>
          <p className='mt-4 text-3xl font-extrabold leading-tight tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
            {t('Everything an agent needs to represent your business')}
          </p>
        </div>

        <div className='mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3'>
          {/* Knowledge — wide tile */}
          <div className='rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-900/5 dark:bg-slate-800/60 dark:ring-white/10 lg:col-span-2'>
            <div className='flex flex-wrap items-baseline justify-between gap-4'>
              <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
                {t('Trained on your knowledge')}
              </h3>
              <div className='flex gap-2 text-xs font-medium text-gray-500 dark:text-slate-400'>
                <span className='rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-900/5 dark:bg-slate-700 dark:ring-0'>
                  {t('Files')} 41
                </span>
                <span className='rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-900/5 dark:bg-slate-700 dark:ring-0'>
                  {t('Links')} 125
                </span>
                <span className='rounded-full bg-white px-2.5 py-1 ring-1 ring-gray-900/5 dark:bg-slate-700 dark:ring-0'>
                  {t('Q&As')} 10
                </span>
              </div>
            </div>
            <p className='mt-3 max-w-xl text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'Websites, PDFs, Word docs, Notion, YouTube, Q&A pairs — connect them once and retrain in one click whenever things change.',
              )}
            </p>
            <div className='mt-6 space-y-2.5'>
              {[
                { name: 'yourstore.com — 125 pages', type: t('Website crawl') },
                { name: 'Product-catalog-2026.pdf', type: 'PDF' },
                { name: t('Help Center'), type: 'Notion' },
                { name: t('Returns & shipping Q&A'), type: 'Q&A' },
              ].map((source) => (
                <div
                  key={source.name}
                  className='flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/5'
                >
                  <span className='truncate font-medium text-gray-900 dark:text-slate-100'>
                    {source.name}
                  </span>
                  <span className='ml-4 flex flex-none items-center gap-2 text-xs text-gray-500 dark:text-slate-400'>
                    {source.type}
                    <svg
                      className='h-4 w-4 text-emerald-500'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                    >
                      <path
                        fillRule='evenodd'
                        d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z'
                        clipRule='evenodd'
                      />
                    </svg>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Models */}
          <div className='flex flex-col rounded-3xl bg-gray-900 p-8 text-white ring-1 ring-white/10 dark:bg-slate-800'>
            <h3 className='text-xl font-bold'>{t('The best AI models')}</h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-400 dark:text-slate-400'>
              {t(
                'Pick the brain behind your agent — switch anytime without retraining.',
              )}
            </p>
            <div className='mt-6 flex flex-wrap gap-2'>
              {['GPT-5', 'Claude', 'Gemini', 'Llama', 'DeepSeek', 'Grok'].map(
                (model, i) => (
                  <span
                    key={model}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-medium ${
                      i === 1
                        ? 'bg-primary-500 text-white'
                        : 'bg-white/10 text-gray-200'
                    }`}
                  >
                    {model}
                  </span>
                ),
              )}
            </div>
            <div className='mt-auto pt-8'>
              <div className='space-y-2.5 border-t border-white/10 pt-6 text-sm text-gray-300'>
                <p className='flex items-center gap-2.5'>
                  <svg
                    className='h-4 w-4 flex-none text-emerald-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
                      clipRule='evenodd'
                    />
                  </svg>
                  {t('Streaming, human-like responses')}
                </p>
                <p className='flex items-center gap-2.5'>
                  <svg
                    className='h-4 w-4 flex-none text-emerald-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
                      clipRule='evenodd'
                    />
                  </svg>
                  {t('Answers grounded in your data')}
                </p>
                <p className='flex items-center gap-2.5'>
                  <svg
                    className='h-4 w-4 flex-none text-emerald-400'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path
                      fillRule='evenodd'
                      d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z'
                      clipRule='evenodd'
                    />
                  </svg>
                  {t('New models added as they launch')}
                </p>
              </div>
            </div>
          </div>

          {/* Analytics */}
          <div className='rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-900/5 dark:bg-slate-800/60 dark:ring-white/10'>
            <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
              {t('Analytics & insights')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'See what customers ask, review every conversation, and get daily summaries in your inbox.',
              )}
            </p>
            <div className='mt-6 flex h-24 items-end gap-2' aria-hidden='true'>
              {[35, 55, 42, 70, 62, 88, 76].map((h, i) => (
                <div
                  key={i}
                  className={`flex-1 rounded-t-md ${
                    i === 5
                      ? 'bg-primary-600'
                      : 'bg-primary-600/25 dark:bg-primary-400/25'
                  }`}
                  style={{ height: `${h}%` }}
                ></div>
              ))}
            </div>
            <p className='mt-4 text-sm font-semibold text-gray-900 dark:text-white'>
              1,284 {t('conversations this week')}
            </p>
          </div>

          {/* Leads */}
          <div className='rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-900/5 dark:bg-slate-800/60 dark:ring-white/10'>
            <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
              {t('Built-in lead capture')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'Your agent collects contact details naturally in conversation.',
              )}
            </p>
            <div className='mt-6 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/5'>
              <div className='space-y-2 text-sm'>
                <div className='flex items-center justify-between'>
                  <span className='text-gray-500 dark:text-slate-400'>
                    {t('Name')}
                  </span>
                  <span className='font-medium text-gray-900 dark:text-white'>
                    Sarah Chen
                  </span>
                </div>
                <div className='flex items-center justify-between'>
                  <span className='text-gray-500 dark:text-slate-400'>
                    {t('Email')}
                  </span>
                  <span className='font-medium text-gray-900 dark:text-white'>
                    sarah@acme.co
                  </span>
                </div>
                <div className='flex items-center justify-between'>
                  <span className='text-gray-500 dark:text-slate-400'>
                    {t('Interest')}
                  </span>
                  <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400'>
                    {t('Demo request')}
                  </span>
                </div>
              </div>
            </div>
            <p className='mt-4 text-sm font-semibold text-gray-900 dark:text-white'>
              23 {t('new leads this week')}
            </p>
          </div>

          {/* White-label */}
          <div className='rounded-3xl bg-gray-50 p-8 ring-1 ring-gray-900/5 dark:bg-slate-800/60 dark:ring-white/10'>
            <h3 className='text-xl font-bold text-gray-900 dark:text-white'>
              {t('Your brand, not ours')}
            </h3>
            <p className='mt-3 text-sm leading-relaxed text-gray-600 dark:text-slate-400'>
              {t(
                'Match your colors, avatar, and tone. Remove Easy-Peasy branding entirely on white-label plans.',
              )}
            </p>
            <div className='mt-6 flex items-center gap-3'>
              {[
                'bg-primary-600',
                'bg-rose-500',
                'bg-emerald-600',
                'bg-amber-500',
                'bg-slate-900 dark:bg-white',
              ].map((color, i) => (
                <span
                  key={i}
                  className={`h-8 w-8 rounded-full ${color} ${
                    i === 0
                      ? 'ring-2 ring-primary-600 ring-offset-2 dark:ring-offset-slate-800'
                      : ''
                  }`}
                ></span>
              ))}
            </div>
            <div className='mt-5 flex items-center justify-between rounded-xl bg-white px-4 py-3 text-sm shadow-sm ring-1 ring-gray-900/5 dark:bg-slate-800 dark:ring-white/5'>
              <span className='font-medium text-gray-900 dark:text-slate-100'>
                {t('“Powered by” badge')}
              </span>
              <span className='relative inline-flex h-5 w-9 items-center rounded-full bg-gray-200 dark:bg-slate-600'>
                <span className='absolute left-0.5 inline-block h-4 w-4 rounded-full bg-white shadow'></span>
              </span>
            </div>
          </div>
        </div>

        {/* Languages strip */}
        <div className='mt-6 flex flex-col items-start justify-between gap-6 overflow-hidden rounded-3xl bg-primary-600 px-8 py-8 sm:flex-row sm:items-center'>
          <div>
            <h3 className='text-xl font-bold text-white'>
              {t('Fluent in 80+ languages')}
            </h3>
            <p className='mt-1 text-sm leading-relaxed text-primary-100'>
              {t(
                'Your agent answers in the language your customer writes — automatically.',
              )}
            </p>
          </div>
          <p className='select-none whitespace-nowrap text-lg font-medium text-primary-200/90'>
            Hello · Hola · Bonjour · こんにちは · 你好 · مرحبا · Olá ·
            안녕하세요
          </p>
        </div>
      </div>
    </section>
  );
}
